import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { slideUp, EASE_OUT } from '@/lib/motion';
import PageHeader from '@/components/common/PageHeader';
import { useCourses, useDeleteCourse, useUpdateCourse } from '../../services/courses';
import { CourseStatus } from '../../types/course';
import { useDebounce } from '../../hooks/useDebounce';
import CourseCard from '../../components/courses/CourseCard';
import CourseIcon from '../../components/icons/CourseIcon';
import VerifiedIcon from '../../components/icons/VerifiedIcon';
import HiddenIcon from '../../components/icons/HiddenIcon';
import CourseLibraryIcon from '../../components/icons/CourseLibraryIcon';
import { PlusIcon } from '@heroicons/react/24/outline';
import terySearchImg from '../../../public/tery/tery_search.png';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';
import { KpiTile } from '@/components/shared/KpiTile';

/** Cards shown at first, and added by each "Show more" (Aleix, 2026-09-10 —
 *  previous/next paging hid the rest of the library behind a control nobody
 *  noticed). */
const PAGE_SIZE = 8;
const ALL_STATUSES: CourseStatus[] = ['draft', 'published', 'archived'];

const CoursesListPage: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  // How many cards are on screen. Growing this re-requests one larger page
  // rather than stitching pages together, which keeps the grid, the counts and
  // "keepPreviousData" behaving exactly as before.
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const searchParam = searchParams.get('search') || '';
  // Single-select status filter, derived from the `status` URL param (at most one).
  const selectedStatus = useMemo<CourseStatus | null>(
    () => ALL_STATUSES.find((s) => searchParams.get('status') === s) ?? null,
    [searchParams]
  );

  const [searchInput, setSearchInput] = useState(searchParam);
  const reduceMotion = useReducedMotion();

  const debouncedSearch = useDebounce(searchInput, 300);

  React.useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (debouncedSearch) {
      params.set('search', debouncedSearch);
      params.set('page', '1');
    setVisibleCount(PAGE_SIZE);
    } else {
      params.delete('search');
    }
    setSearchParams(params, { replace: true });
  }, [debouncedSearch]);

  const queryParams = useMemo(
    () => ({
      page: 1,
      per_page: visibleCount,
      search: debouncedSearch || undefined,
      status: selectedStatus ?? undefined,
    }),
    [visibleCount, debouncedSearch, selectedStatus]
  );

  const { data, isLoading, isFetching, error } = useCourses(queryParams);
  const deleteMutation = useDeleteCourse();
  const updateMutation = useUpdateCourse();

  // Single-select: clicking the active tile clears the filter (show all);
  // clicking another replaces it. At most one `status` ever rides the URL.
  const selectStatus = (status: CourseStatus) => {
    const params = new URLSearchParams(searchParams);
    params.delete('status');
    if (selectedStatus !== status) params.set('status', status);
    params.set('page', '1');
    setVisibleCount(PAGE_SIZE);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchInput('');
    const params = new URLSearchParams(searchParams);
    params.delete('status');
    params.delete('search');
    params.set('page', '1');
    setVisibleCount(PAGE_SIZE);
    setSearchParams(params);
  };

  const handleView = (courseId: string) => {
    navigate(`/courses/${courseId}/preview`);
  };

  const handleEdit = (courseId: string) => {
    navigate(`/courses/${courseId}/edit`);
  };

  const handleConfigure = (courseId: string) => {
    navigate(`/courses/${courseId}/configure`);
  };

  const handleHide = async (courseId: string) => {
    const course = courses.find((c) => c.id === courseId);
    if (!course) return;
    const nextStatus: CourseStatus = course.status === 'archived' ? 'published' : 'archived';
    try {
      await updateMutation.mutateAsync({ courseId, data: { status: nextStatus } });
    } catch {
      // handled by mutation
    }
  };

  const handleDelete = async (courseId: string) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      try {
        await deleteMutation.mutateAsync(courseId);
      } catch {
        // handled by mutation
      }
    }
  };

  const handleCreate = () => {
    navigate('/courses/create');
  };

  const courses = Array.isArray(data?.courses) ? data!.courses : [];
  const shownCount = courses.length;
  const totalCount = data?.pagination?.total ?? shownCount;
  const hasMore = shownCount < totalCount;
  // Counts come from the API (true per-status totals across all pages), so the
  // tiles never collapse to 0 while a filter is active.
  const { draft: draftCount, published: publishedCount, archived: archivedCount } =
    data?.statusCounts ?? { draft: 0, published: 0, archived: 0 };
  const hasActiveFilters = Boolean(searchInput) || selectedStatus !== null;

  // Filter-switch / pagination transition: one quick container fade — the new
  // result set appears together, immediately. (The old exit-wait + per-card
  // stagger stacked up to ~0.9s on a full page and read as friction.)
  const gridContainer = {
    hidden: reduceMotion ? { opacity: 1 } : { opacity: 0, y: 6 },
    visible: {
      opacity: 1,
      y: 0,
      transition: reduceMotion ? { duration: 0 } : { duration: 0.18, ease: EASE_OUT },
    },
  };
  // Items ride the container; no per-card delay on a swap the user initiated.
  const gridItem = { hidden: {}, visible: {} };

  // Re-key the grid on the actual result set (not the filter intent) so the
  // cross-fade fires when the cards truly change. With keepPreviousData this
  // keeps the previous cards on screen during the fetch (the "Updating…" cue
  // covers it), then swaps them in a single clean transition.
  const gridKey = courses.length ? courses.map((c) => c.id).join(',') : 'empty';

  return (
    <motion.div {...slideUp}>
      <PageHeader
        title="Course Library"
        className="pb-4 mb-8"
        trail={selectedStatus ? [{ label: 'Course Library', to: '/courses' }] : undefined}
        leaf={
          selectedStatus
            ? { draft: 'Drafts', published: 'Published', archived: 'Hidden' }[selectedStatus]
            : undefined
        }
        actions={
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold bg-primary-500 rounded-full hover:bg-primary-600 dark:hover:bg-primary-400 transition-colors active:scale-95 shrink-0"
            data-testid="courses-create-button"
          >
            <PlusIcon className="w-5 h-5" />
            <span className="hidden sm:inline">Create Course</span>
          </button>
        }
      />

      {/* Search + Filter bar */}
      <div className="flex flex-col sm:flex-row items-center gap-3 mb-10">
        <img
          src={terySearchImg}
          alt=""
          className="w-[60px] h-[60px] object-contain -scale-x-100 pointer-events-none select-none shrink-0 hidden sm:block"
        />
        <input
          type="text"
          placeholder="Search courses by title..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="flex-1 h-11 px-4 text-sm bg-white dark:bg-grey-900 border border-border-primary/20 rounded-xl outline-none focus:border-primary-500 text-text-primary placeholder:text-text-tertiary transition-colors"
          data-testid="courses-search-input"
        />
      </div>

      {/* Error */}
      {error && (
        <div
          className="p-5 rounded-2xl bg-error-50 border border-error-200 mb-8"
          data-testid="courses-error"
        >
          <p className="text-sm text-error-700">Failed to load courses. Please try again later.</p>
        </div>
      )}

      <phantom-ui
        {...phantomProps(theme === 'dark', isLoading)}
        data-testid={isLoading ? 'courses-loading' : undefined}
      >
        {/* Loading placeholder cards */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-[468px] rounded-[28px]" />
            ))}
          </div>
        )}

        {/* Content — the status filter tiles always render (even with zero
            courses) so an admin can always see and click into Drafts /
            Published / Hidden; the grid or an empty state shows below. */}
        {!isLoading && !error && (
          <div className="flex flex-col gap-10">
            {/* KPI cards bar — each tile is a single-select status filter
                (clicking the active one clears it). Counts are true totals
                from the API, so the tiles persist (showing 0) even when there
                are no courses or a filter returns nothing. */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* Drafts */}
              <KpiTile
                title={`${draftCount} ${draftCount === 1 ? 'Draft' : 'Drafts'}`}
                description="Courses in progress"
                selected={selectedStatus === 'draft'}
                ariaLabel="Filter by drafts"
                onClick={() => selectStatus('draft')}
                iconComponent={{
                  icon: <CourseIcon />,
                }}
              />

              {/* Published */}
              <KpiTile
                title={`${publishedCount} Published`}
                description="Live for learners"
                selected={selectedStatus === 'published'}
                ariaLabel="Filter by published"
                onClick={() => selectStatus('published')}
                iconComponent={{
                  icon: <VerifiedIcon />,
                }}
              />

              {/* Hidden */}
              <KpiTile
                title={`${archivedCount} Hidden`}
                description="Hidden from Learners"
                selected={selectedStatus === 'archived'}
                ariaLabel="Filter by hidden"
                onClick={() => selectStatus('archived')}
                iconComponent={{
                  icon: <HiddenIcon color={'rgb(var(--color-primary-400))'} accentColor={'rgb(var(--color-primary-100))'} />,
                }}
              />
            </section>

            {/* Revalidation cue — shown while refetching with previous data still
                on screen (keepPreviousData), so a filter/page switch never looks
                frozen. The global reduced-motion rule freezes the spinner. */}
            {isFetching && !isLoading && (
              <div
                data-testid="courses-refetching"
                aria-live="polite"
                className="flex items-center gap-2 -mt-2 text-sm font-semibold text-text-secondary"
              >
                <span className="h-3.5 w-3.5 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
                Updating…
              </div>
            )}

            {courses.length === 0 ? (
              /* Empty state — shown beneath the always-visible status tiles.
                 Distinguishes "no courses at all" from "filter matched none". */
              <div
                className="flex flex-col items-center justify-center py-16"
                data-testid="courses-empty-state"
              >
                <div className="w-20 h-20 rounded-full bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center mb-5">
                  <CourseLibraryIcon className="w-10 h-10 text-primary-500" />
                </div>
                {hasActiveFilters ? (
                  <>
                    <h2 className="text-xl font-bold text-text-primary mb-2">
                      No Courses Match Your Filters
                    </h2>
                    <p className="text-sm text-text-secondary mb-6 text-center max-w-sm">
                      Try a different search or status.
                    </p>
                    <button
                      onClick={clearFilters}
                      className="text-sm font-bold text-primary-600 hover:text-primary-700 dark:text-primary-350 dark:hover:text-primary-300 transition-colors active:scale-95"
                    >
                      Clear filters
                    </button>
                  </>
                ) : (
                  <>
                    <h2 className="text-xl font-bold text-text-primary mb-2">No Courses Yet</h2>
                    <p className="text-sm text-text-secondary mb-6 text-center max-w-sm">
                      Get started by creating your first course.
                    </p>
                    <button
                      onClick={handleCreate}
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold bg-primary-500 rounded-full hover:bg-primary-600 dark:hover:bg-primary-400 transition-colors"
                    >
                      <PlusIcon className="w-5 h-5" />
                      Create Course
                    </button>
                  </>
                )}
              </div>
            ) : (
              <>
                {/* One grid of courses (filtered server-side, or all when no
                    status is selected). Each card carries its own status tag.
                    The grid is keyed on the result set so a filter/page switch
                    replays one quick fade — no exit-wait, no stagger. */}
                <motion.div
                  key={gridKey}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                  data-testid="courses-grid"
                  variants={gridContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {courses.map((course) => (
                    <motion.div key={course.id} variants={gridItem}>
                      <CourseCard
                        course={course}
                        onView={handleView}
                        onEdit={handleEdit}
                        onConfigure={handleConfigure}
                        onHide={handleHide}
                        onDelete={handleDelete}
                        data-testid={`course-card-${course.id}`}
                      />
                    </motion.div>
                  ))}
                </motion.div>

                {/* Show more — the library grows in place, eight at a time. */}
                {hasMore && (
                  <div
                    className="flex flex-col items-center gap-2 pt-6"
                    data-testid="courses-pagination"
                  >
                    <button
                      type="button"
                      onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                      disabled={isFetching}
                      className="px-5 py-2 text-sm font-bold text-text-primary border border-border-primary/40 rounded-full hover:bg-text-primary/[0.06] active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed focus-ring transition-[background-color,transform] duration-150"
                      data-testid="courses-show-more"
                    >
                      {isFetching ? 'Loading…' : 'Show more'}
                    </button>
                    <p className="text-xs text-text-secondary">
                      Showing {shownCount} of {totalCount}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </phantom-ui>
    </motion.div>
  );
};

export default CoursesListPage;
